import { supabase } from '../lib/supabase';

export interface AdminData {
  id: string;
  username: string;
  role: 'authenticated';
}

export async function authenticateAdmin(username: string, password: string): Promise<AdminData | null> {
  try {
    if (!username || !password) {
      console.error('Username and password are required');
      return null;
    }

    // Call the RPC function with improved error handling
    const { data, error } = await supabase.rpc('authenticate_admin', {
      p_username: username,
      p_password: password
    });

    // Debug logging
    console.log('Auth attempt for username:', username);
    
    if (error) {
      console.error('Authentication failed:', {
        message: error.message,
        details: error.details,
        hint: error.hint
      });
      return null;
    }

    if (!data) {
      console.error('No data returned from authentication');
      return null;
    }

    // Check for error response from function
    if (data.error) {
      console.error('Database function error:', data.error);
      return null;
    }

    // Validate the response structure
    if (!data.id || !data.username || data.role !== 'authenticated') {
      console.error('Invalid response structure:', data);
      return null;
    }

    return {
      id: data.id,
      username: data.username,
      role: 'authenticated'
    } as AdminData;

  } catch (err) {
    console.error('Authentication error:', err);
    return null;
  }
}

export function getAdminSession(): AdminData | null {
  const adminData = localStorage.getItem('admin');
  return adminData ? JSON.parse(adminData) : null;
}

export function isAuthenticated(): boolean {
  const admin = getAdminSession();
  return Boolean(admin?.role === 'authenticated');
}

// Headers that let a Netlify function re-verify (server-side) that the
// current visitor is really a logged in admin, used to gate admin-only
// backend endpoints such as /api/verseAccess and /api/siteDesign (POST).
export function getAdminAuthHeaders(): Record<string, string> {
  const admin = getAdminSession();
  if (!admin) return {};
  return {
    'x-admin-id': admin.id,
    'x-admin-username': admin.username,
  };
}
